// assets/AllCourses/Exercises/FillInTheBlanksExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function FillInTheBlanksExercise({
  exercise = {},
  onUpdate,           // Edit mode only
  errors = {},
  isPreview = false,  // false = admin edit, true = student view
  showResult = false,
  onComplete = () => {}, // Student: report correct/incorrect
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  // Safe questions
  const questions = Array.isArray(exercise.fillInTheBlanksQuestions)
    ? exercise.fillInTheBlanksQuestions
    : [];

  // Student state
  const [state, setState] = useState({
    questions: [],
    hasInteracted: false,
  });
  const completedRef = useRef(false);

  // Initialize student state
 // Inside FillInTheBlanksExercise.jsx (student mode)

useEffect(() => {
  if (!isPreview || !exercise.attempt) {
    // Build fresh state
    const initial = {
      questions: questions.map(q => ({
        userAnswers: Array((q.sentence.match(/_+/g) || []).length).fill("")
      })),
      hasInteracted: false,
    };
    setState(initial);
    return;
  }

  // BACKWARD COMPATIBILITY: handle old attempt format
  if (Array.isArray(exercise.attempt.answers)) {
    // Old format: { answers: [ ["a"], ["b"] ], hasInteracted: true }
    const oldAnswers = exercise.attempt.answers;
    setState({
      questions: questions.map((q, i) => ({
        userAnswers: oldAnswers[i] || Array((q.sentence.match(/_+/g) || []).length).fill("")
      })),
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  } else if (exercise.attempt.questions) {
    // New format
    setState(exercise.attempt);
  } else {
    // Fallback
    setState({
      questions: questions.map(q => ({
        userAnswers: Array((q.sentence.match(/_+/g) || []).length).fill("")
      })),
      hasInteracted: false,
    });
  }
}, [isPreview, questions, exercise.attempt]);

  // Reset on exercise change
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise._id, isPreview]);

  // === EDIT MODE FUNCTIONS ===
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("fillInTheBlanksQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;

    const next = [...questions];
    const current = next[qIdx] || {
      sentence: "",
      answers: [],
      requireCapitalization: false,
    };

    let updated = { ...current, [field]: value };

    if (field === "sentence") {
      const blanks = (value.match(/_+/g) || []).length;
      const oldAnswers = Array.isArray(current.answers) ? current.answers : [];
      updated.answers = Array.from({ length: blanks }, (_, i) => oldAnswers[i] || "");
    }

    next[qIdx] = updated;
    updateQuestions(next);
  };

  const handleAnswerChange = (qIdx, aIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx] || { answers: [] };
    const answers = [...q.answers];
    answers[aIdx] = value;
    next[qIdx] = { ...q, answers };
    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        sentence: "",
        answers: [],
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  // === STUDENT MODE FUNCTIONS ===
  const handleInputChange = (qIdx, blankIdx, value) => {
    if (isEditMode || showResult) return;

    setState((prev) => {
      const next = structuredClone(prev);
      next.questions[qIdx].userAnswers[blankIdx] = value;
      next.hasInteracted = true;

      setAttempt(next);

      const allFilled = next.questions.every((qq) =>
        qq.userAnswers.every((a) => a.trim() !== "")
      );

      if (allFilled) {
        checkAnswers(next);
      }

      return next;
    });
  };

  const checkAnswers = (currentState) => {
    if (completedRef.current || !isPreview) return;
    completedRef.current = true;

    const allCorrect = questions.every((q, qIdx) => {
      const blanks = (q.sentence.match(/_+/g) || []).length;
      return Array.from({ length: blanks }).every((_, aIdx) => {
        const user = String(currentState.questions[qIdx]?.userAnswers[aIdx] || "").trim();
        const correct = String(q.answers[aIdx] || "").trim();

        if (q.requireCapitalization) {
          return user === correct;
        }
        return user.toLowerCase() === correct.toLowerCase();
      });
    });

    onComplete(exercise._id, allCorrect);
  };

  // === EDIT MODE RENDER ===
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No questions yet. Click "Add Question" to begin.
          </p>
        )}

        {questions.map((q, qIdx) => {
          const blanks = (q.sentence.match(/_+/g) || []).length;

          return (
            <Card key={qIdx} className="p-6 space-y-6">
              <TextareaField
                label={`Question ${qIdx + 1} (use _ for blanks)`}
                value={q.sentence || ""}
                onChange={(v) => handleQuestionChange(qIdx, "sentence", v)}
                placeholder="The ___ is shining brightly today."
                rows={4}
                required
                error={errors?.[`exercise-${exercise.index}-sentence-${qIdx}`]}
              />

              {/* Answers */}
              <div className="space-y-3">
                <label className="font-medium text-sm">Correct Answers</label>
                {blanks === 0 ? (
                  <p className="text-sm text-gray-500">
                    Use one or more underscores (_) in the sentence to create blanks
                  </p>
                ) : (
                  Array.from({ length: blanks }).map((_, aIdx) => (
                    <StringField
                      key={aIdx}
                      label={`Answer for blank ${aIdx + 1}`}
                      value={q.answers[aIdx] || ""}
                      onChange={(v) => handleAnswerChange(qIdx, aIdx, v)}
                      placeholder="sun"
                      required
                      error={errors?.[`exercise-${exercise.index}-answers-${qIdx}-${aIdx}`]}
                    />
                  ))
                )}
              </div>

              <CapitalizationRule
                checked={!!q.requireCapitalization}
                onChange={(c) => handleCapitalizationChange(qIdx, c)}
              />

              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleRemoveQuestion(qIdx)}
              >
                Remove Question
              </Button>
            </Card>
          );
        })}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  // === STUDENT PREVIEW MODE ===
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No fill-in-the-blanks questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardDescription>
          Fill in the blanks with the correct words.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const parts = q.sentence.split(/(_+)/g).filter(Boolean);
      const qState = state.questions?.[qIdx] || { userAnswers: [] };
          let blankIndex = 0;

          return (
            <div
              key={qIdx}
              className="bg-muted p-6 rounded-lg text-base leading-loose flex flex-wrap items-center gap-3"
            >
              {parts.map((part, i) => {
                if (/_+/.test(part)) {
                  const bIdx = blankIndex++;
                  const userValue = qState.userAnswers[bIdx] || "";
                  const correctAnswer = q.answers[bIdx] || "";
                  const isCorrect =
                    showResult &&
                    (q.requireCapitalization
                      ? userValue.trim() === correctAnswer.trim()
                      : userValue.trim().toLowerCase() === correctAnswer.trim().toLowerCase());

                  return (
                    <div key={i} className="inline-flex flex-col items-center">
                      <Input
                        value={userValue}
                        onChange={(e) => handleInputChange(qIdx, bIdx, e.target.value)}
                        placeholder="____"
                        className={cn(
                          "w-32 text-center",
                          showResult && isCorrect && "border-green-500 bg-green-50",
                          showResult && !isCorrect && userValue && "border-red-500 bg-red-50"
                        )}
                        disabled={showResult}
                      />
                      {showResult && (
                        <span className="text-xs mt-1">
                          {isCorrect ? "Correct" : `Wrong → ${correctAnswer}`}
                        </span>
                      )}
                    </div>
                  );
                }
                return <span key={i}>{part}</span>;
              })}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}